import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BroadcastForm from "./BroadcastForm.jsx";
import { createCrmBroadcast, updateCrmBroadcast, getCrmBroadcast } from "@/api/crm";
import InlineLoader from "@/components/InlineLoader.jsx";

const BroadcastFormWrapper = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [broadcastData, setBroadcastData] = useState(null);
  const [error, setError] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  
  // Fetch broadcast data if editing
  useEffect(() => {
    if (id && id !== 'new') {
      const fetchBroadcast = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await getCrmBroadcast(id);
          if (response.status === "success") {
            // Handle the nested structure from the API response
            const emailData = response.email || {};
            const mauticEmailData = response.mautic_email || {};
            
            // Helper function to decode HTML content with Unicode escapes
            const decodeHtml = (html) => {
              if (!html) return "";
              try {
                // Replace Unicode escape sequences with their actual characters
                return html.replace(/\u([0-9a-fA-F]{4})/g, (match, hex) => {
                  return String.fromCharCode(parseInt(hex, 16));
                });
              } catch (error) {
                return html;
              }
            };

            // Helper function to parse date safely
            const parseDate = (dateString) => {
              if (!dateString) return null;
              try {
                const date = new Date(dateString);
                return isNaN(date.getTime()) ? null : date;
              } catch (error) {
                return null;
              }
            };

            // Map API fields to form fields
            const mappedData = {
              name: mauticEmailData.name || emailData.name || "",
              description: mauticEmailData.description || "",
              subject: mauticEmailData.subject || emailData.subject || "",
              emailContent: decodeHtml(mauticEmailData.html || ""),
              emailDesign: mauticEmailData.design || null,
              excludeHeader: mauticEmailData.exclude_header || false,
              previewText: mauticEmailData.preview_text || "",
              sendAsap: !mauticEmailData.schedule && !emailData.publishUp,
              isPublished: mauticEmailData.is_published || false,
              scheduledDate: (() => {
                const scheduleDate = parseDate(mauticEmailData.schedule) || parseDate(emailData.publishUp);
                return scheduleDate ? scheduleDate.toISOString().split('T')[0] : "";
              })(),
              scheduledTime: (() => {
                const scheduleDate = parseDate(mauticEmailData.schedule) || parseDate(emailData.publishUp);
                return scheduleDate ? scheduleDate.toTimeString().slice(0, 5) : "";
              })(),
              emailId: emailData.id || mauticEmailData.mautic_id || null,
            };
            
            setBroadcastData(mappedData);

            const scheduledAt = parseDate(mauticEmailData.schedule) || parseDate(emailData.publishUp);
            const now = new Date();
            const isSent = (mappedData.isPublished) && (!scheduledAt || scheduledAt <= now);
            setReadOnly(isSent);
          } else {
            setError("Failed to load broadcast data");
          }
        } catch (error) {
          let errorMessage = "Failed to load broadcast data";
          
          // Try to parse the error response to get the server's message
          if (error?.response) {
            try {
              const responseData = e.response?.data
              if (responseData?.message) {
                errorMessage = responseData.message;
              }
            } catch (parseError) {
            }
          }
          
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      };
      
      fetchBroadcast();
    }
  }, [id]);
  
  if (id === 'new') {
    return (
      <BroadcastForm 
        onSave={async (payload, shouldClose = true) => {
          try {
            const result = await createCrmBroadcast(payload);
            if (shouldClose) {
              navigate('/crm/broadcasts');
            }
          } catch (error) {
            let errorMessage = 'Failed to save broadcast. Please try again.';
            
            // Try to parse the error response to get the server's message
            if (error?.response) {
              try {
                const responseData = e.response?.data
                if (responseData?.message) {
                  errorMessage = responseData.message;
                }
              } catch (parseError) {
              }
            }
          }
        }}
        onCancel={() => navigate('/crm/broadcasts')}
      />
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-2xl mx-auto">
        <InlineLoader message="Loading broadcast..." minHeight="h-64" />
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-2xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }
  
  // Show form with fetched data
  return (
    <BroadcastForm 
      initialData={broadcastData || {}}
      readOnly={readOnly}
      onSave={async (payload, shouldClose = true) => {
        try {
          const result = await updateCrmBroadcast(id, payload);
          if (shouldClose) {
            navigate('/crm/broadcasts');
          }
        } catch (error) {
          let errorMessage = 'Failed to update broadcast. Please try again.';
          
          // Try to parse the error response to get the server's message
          if (error?.response) {
            try {
              const responseData = e.response?.data
              if (responseData?.message) {
                errorMessage = responseData.message;
              }
            } catch (parseError) {
            }
          }
        }
      }}
      onCancel={() => navigate('/crm/broadcasts')}
    />
  );
};

export default BroadcastFormWrapper; 